// client/src/pages/Checkout.js
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useSettings } from '../hooks/useSettings.js';
import { useAuth } from '../hooks/useAuth.js';
import api from '../services/api.js';
import CheckoutForm from '../components/ui/CheckoutForm.js';
import ProgressSteps from '../components/ui/ProgressSteps.js';

const progressSteps = [
  { id: 'account', label: 'Account' },
  { id: 'choose-plan', label: 'Choose Plan' },
  { id: 'payment', label: 'Payment' },
];

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#D4AF37',
    colorBackground: '#0f0f2e',
    colorText: '#e2e8f0',
    colorTextSecondary: '#8b8bb5',
    colorDanger: '#ef4444',
    fontFamily: '"Lora", serif',
    borderRadius: '10px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': { border: '1px solid #3b3b6b', backgroundColor: '#0f0f2e', color: '#e2e8f0', padding: '12px' },
    '.Input:focus': { border: '1px solid #D4AF37', boxShadow: '0 0 0 1px #D4AF37' },
    '.Input::placeholder': { color: '#6b6b9b' },
    '.Label': { color: '#a5a5d0', fontSize: '14px' },
    '.Tab': { border: '1px solid #3b3b6b', backgroundColor: '#1a1a3e', color: '#a5a5d0' },
    '.Tab--selected': { border: '1px solid #D4AF37', backgroundColor: '#1a1a3e', color: '#D4AF37' },
    '.Tab:hover': { color: '#D4AF37' },
  },
};

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userTypes } = useSettings();
  const { user, loading: authLoading } = useAuth();

  const [clientSecret, setClientSecret] = useState(null);
  const [tier, setTier] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [taxInfo, setTaxInfo] = useState(null);

  const pendingPlan = (() => {
    try {
      const stored = sessionStorage.getItem('pendingPlan');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })();

  const tierId = parseInt(searchParams.get('tier')) || pendingPlan?.tier;
  const interval = searchParams.get('interval') || pendingPlan?.interval || 'monthly';

  useEffect(() => {
    if (tierId && interval) sessionStorage.removeItem('pendingPlan');
  }, [tierId, interval]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/login?redirect=${encodeURIComponent(`/checkout?tier=${tierId}&interval=${interval}`)}`);
    }
  }, [authLoading, user, navigate, tierId, interval]);

  useEffect(() => {
    if (!userTypes || !tierId) return;
    const found = userTypes.find((t) => t.usertypeid === tierId);
    if (found) { setTier(found); } else { setError('Plan not found. Please go back and select a plan.'); }
  }, [userTypes, tierId]);

  // Check if user is already on this plan (but don't auto-create subscription)
  useEffect(() => {
    if (!tier || !user) return;
    if (user.usertype === tier.usertypeid) { setError('You are already subscribed to this plan.'); }
  }, [tier, user]);

  // Called by CheckoutForm after billing address is collected
  const createSubscription = async (billingAddress) => {
    if (creating || clientSecret) return;
    setCreating(true);
    try {
      const data = await api.post('/subscriptions/create-subscription', {
        tier: tier.usertypeid,
        interval,
        billingAddress,
      });
      setClientSecret(data.clientSecret);
      setTaxInfo({
        subtotal: data.subtotal,
        taxAmount: data.taxAmount,
        total: data.total,
        currency: data.currency,
      });
    } catch (err) {
      setError(err.data?.message || err.data?.error || 'Unable to initialize checkout. Please try again.');
    } finally { setCreating(false); }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="bg-royal-800 border border-royal-600 rounded-2xl p-8">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-royal-300 mb-6">{error}</p>
            <Link to="/pricing" className="text-gold hover:text-gold-light transition-colors">
              &larr; Back to Pricing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!tier) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-royal-500 dark:text-royal-400">Loading plan details...</p>
        </div>
      </div>
    );
  }

  // If we have a clientSecret, show the Elements form for payment
  if (clientSecret) {
    const elementsOptions = {
      clientSecret,
      appearance,
      fonts: [
        { cssSrc: 'https://fonts.googleapis.com/css2?family=Lora:wght@400;600&display=swap' },
      ],
    };

    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-lg mx-auto px-4 py-12">
          <ProgressSteps steps={progressSteps} currentStep="payment" />

          <div className="text-center mb-8">
            <h1 className="font-display text-3xl text-gold-dark dark:text-gold mb-2">Subscribe to {tier.typename}</h1>
            <p className="text-royal-500 dark:text-royal-400">{tier.tagline}</p>
          </div>

          <div className="bg-royal-800 border border-royal-600 rounded-2xl p-6 sm:p-8">
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm tier={tier} interval={interval} step="payment" taxInfo={taxInfo} />
            </Elements>
          </div>

          <div className="text-center mt-6">
            <Link to="/pricing" className="text-royal-500 dark:text-royal-400 hover:text-gold-dark dark:hover:text-gold transition-colors text-sm">
              &larr; Back to Pricing
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No clientSecret yet — show billing address collection step
  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-lg mx-auto px-4 py-12">
        <ProgressSteps steps={progressSteps} currentStep="payment" />

        <div className="text-center mb-8">
          <h1 className="font-display text-3xl text-gold-dark dark:text-gold mb-2">Subscribe to {tier.typename}</h1>
          <p className="text-royal-500 dark:text-royal-400">{tier.tagline}</p>
        </div>

        <div className="bg-royal-800 border border-royal-600 rounded-2xl p-6 sm:p-8">
          <CheckoutForm
            tier={tier}
            interval={interval}
            step="address"
            onAddressComplete={createSubscription}
            creating={creating}
          />
        </div>

        <div className="text-center mt-6">
          <Link to="/pricing" className="text-royal-500 dark:text-royal-400 hover:text-gold-dark dark:hover:text-gold transition-colors text-sm">
            &larr; Back to Pricing
          </Link>
        </div>
      </div>
    </div>
  );
}